import { css, cx } from "@emotion/css";
import {
  Box,
  Collapse,
  Colors,
  Icons,
  Table,
  tableCellClasses,
  tableRowClasses,
  Tooltip,
  tooltipClasses,
  Typography,
  IconButton,
  Divider,
} from "@analyticsMD/quil";
import { Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { CircularProgress } from "@mui/material";

import {
  styles as commonStyles,
  sxStyles as commonSxStyles,
} from "./common-styles";

import {
  FC,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import moment from "moment";

import APIContext from "../../contexts/APIContext";
import Milestone from "../../types/Milestone";
import MilestonesModal, {
  MilestonesModalHandle,
} from "../milestones/milestones-modal/milestones-modal";
import {
  patchPatientMilestone,
  postPatientMilestones,
} from "../../api/endpoints";
import { useMutation, useQueryClient } from "react-query";

import MilestoneItem from "../milestones/milestone-item";
import SectionGrid from "../section-grid";
import { Sources } from "../../types/Sources";
import { MilestonesSectionConfigurations } from "../../types/MilestonesSectionConfigurations";
import { MilestoneStatusChoices } from "../../types/MilestoneStatusChoices";
import Configuration from "../../types/Configuration";
import PatientMilestone from "../../types/PatientMilestone";
import { deepPurple, grey, red } from "@mui/material/colors";
import { UI_ACCENT_BLUE } from "../../constants/ui-colors";

const styles = {
  ...commonStyles,
  milestonesSection: css`
    @media (min-height: 1000px) {
      height: 100%;
    }
    background-color: ${Colors.white};
    padding: 0rem 1rem;
  `,
  headerIcon: css`
    font-size: 0.75rem;
    margin-right: 0.5rem;
  `,
  expandCollapseIcon: css`
    color: ${Colors.grey_40};
    margin-left: 0.5rem;
  `,
  emptyImg: css`
    height: 15rem;
    width: 15rem;
    margin: -3rem 0 -3rem 1rem;
  `,
  emptyMsg: css`
    margin-bottom: 0;
    color: ${Colors.grey_40};
    width: 50%;
    text-align: center;
    font-size: 0.9rem;
  `,
  emptyWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2rem;
    margin-bottom: 2rem;
  `,
  deleteIcon: css`
    margin-left: -0.45rem;
    margin-right: 0.3rem;
    font-size: 1.05rem;
    color: transparent;
    width: 1.2rem !important;
    height: 1.2rem !important;
  `,
  icon: css`
    font-size: 1.25rem;
  `,
  row: css`
    &:hover {
      background-color: transparent;
    }
  `,
  // Doesn't shrink, so a tight row wraps the name (and its open flag) instead.
  milestoneTimestamp: css`
    color: ${Colors.grey_30};
    font-size: 0.65rem;
    font-weight: normal;
    margin-right: 1rem;
    flex-shrink: 0;
  `,
};

const sxStyles = {
  ...commonSxStyles,
  addButton: (showMilestonesModal) => {
    return {
      color: UI_ACCENT_BLUE,
      // when the modal opens, the button's top border still peeks out, this "hides" the button visually
      borderColor: showMilestonesModal ? Colors.white : UI_ACCENT_BLUE,
      textTransform: "none",
      width: "100%",
      "&:hover": {
        borderColor: UI_ACCENT_BLUE,
        color: UI_ACCENT_BLUE,
        backgroundColor: alpha(UI_ACCENT_BLUE, 0.04),
      },
      "&.Mui-focusVisible": {
        borderColor: UI_ACCENT_BLUE,
      },
    };
  },
  addMilestoneWrapper: {
    margin: "auto",
  },
  buttonWrapper: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
  },
  headingWithIcon: {
    svg: {
      color: Colors.white,
      backgroundColor: Colors.qpurple_alt_40,
      borderRadius: "0.25rem",
      padding: "0.25rem",
    },
  },
  table: (disableEdit: boolean) => ({
    width: "100%",
    [`& .${tableCellClasses.root}`]: {
      width: "fit-content",
      textAlign: "left",
      cursor: disableEdit ? "default" : "pointer",
      borderBottom: `0.5px solid ${grey[300]}`,
      paddingTop: "7px",
      paddingBottom: "7px",
      paddingLeft: 0,
      paddingRight: 0,
      verticalAlign: "top",
    },
    [`& .${tableRowClasses.root}`]: {
      "&:hover": {
        "#delete-icon": {
          color: grey[900],
        },
        "#delete-icon-progress": {
          color: grey[900],
        },
      },
      [`& .${tableCellClasses.root}:first-of-type`]: {
        paddingTop: "7px",
        paddingBottom: "7px",
        paddingLeft: 0,
        paddingRight: "0.5rem",
        width: "auto",
        minWidth: "1.5rem",
        borderBottom: "none",
        verticalAlign: "top",
        overflow: "visible",
      },
    },
  }),
  tableBottom: {
    [`& .${tableRowClasses.root}:nth-last-of-type(2)`]: {
      [`& .${tableCellClasses.root}`]: {
        border: "none",
        paddingBottom: 0,
      },
    },
  },
  icon: {
    color: Colors.good_60,
    paddingRight: "1rem",
  },
  emptyIcon: {
    height: "1rem",
    width: "1rem",
    paddingRight: "1rem",
  },
  key: (tooltipEnabled: boolean) => ({
    fontSize: "0.875rem",
    fontWeight: "400",
    lineHeight: 1.25,
    margin: 0,
    color: Colors.grey_65,
    textDecoration: tooltipEnabled ? "underline" : "none",
    textDecorationStyle: tooltipEnabled ? "dashed" : "none",
    textUnderlineOffset: tooltipEnabled ? "0.2rem" : "0",
  }),
  // The flag wraps under the name when the two don't fit on one line.
  nameWrapper: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "0.3rem",
    marginRight: "auto",
  },
  openFlag: {
    display: "flex",
    alignItems: "center",
    gap: "0.375rem",
    color: red[800],
    whiteSpace: "nowrap",
  },
  openFlagIcon: {
    width: "0.875rem",
    height: "0.875rem",
  },
  openFlagText: {
    fontSize: "0.8rem",
    lineHeight: 1.25,
    margin: 0,
    color: "inherit",
  },
  value: {
    fontSize: "0.769rem",
    color: Colors.grey_65,
    marginLeft: "1rem",
  },
  secondaryValue: {
    fontSize: "0.8rem",
    textAlign: "right",
    color: Colors.grey_65,
    paddingTop: "0.15rem",
  },
  refreshIcon: {
    height: "1rem",
    width: "1rem",
    paddingTop: "0.15rem",
    paddingRight: "0.25rem",
    color: Colors.grey_30,
  },
  valueWrapper: {
    display: "flex",
    alignItems: "flex-start",
  },
  deleteIconWrapper: {
    paddingTop: "0.3rem",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  tooltip: {
    width: "50% !important",
    bottom: "0.35rem",
    marginLeft: "0.2rem !important",
    fontSize: "0.625rem",
    textAlign: "center",
    [`& .${tooltipClasses.arrow}`]: {
      top: "0.2rem !important",
    },
  },
  milestoneTooltip: {
    fontSize: "0.8rem",
    bottom: "0.4rem",
    [`&.${tooltipClasses.tooltip}`]: {
      width: "80%",
      backgroundColor: Colors.grey_80,
      color: Colors.white,
      borderRadius: "0.2rem !important",
      boxShadow: "none",
      paddingTop: "0.4rem",
      paddingBottom: "0.4rem",
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: Colors.grey_80,
      top: "0.2rem !important",
    },
  },
};

const MAX_INITIAL_ROWS = 5;
const CLOCK_TICK_MS = 60 * 1000;

// Order patient milestones by their milestone's sort_order, falling back to
// display_name for stable, alphabetical tiebreaks.
const bySortOrder = (a: PatientMilestone, b: PatientMilestone) =>
  a.milestone.sort_order === b.milestone.sort_order
    ? a.milestone.display_name.localeCompare(b.milestone.display_name)
    : a.milestone.sort_order - b.milestone.sort_order;

const isIncomplete = (pm: PatientMilestone) =>
  pm.milestone_status === MilestoneStatusChoices.INCOMPLETE;

const isDisplayableMilestone = (pm: PatientMilestone) =>
  !pm.milestone.is_clinical_marker &&
  (pm.milestone_status === MilestoneStatusChoices.INCOMPLETE ||
    pm.milestone_status === MilestoneStatusChoices.COMPLETED ||
    pm.milestone_status === MilestoneStatusChoices.ALWAYS_NORMAL);

interface MilestonesSectionProps {
  configuration: Configuration;
}

const MilestonesSection: FC<MilestonesSectionProps> = ({ configuration }) => {
  const [showSection, setShowSection] = useState(true);
  const [showMilestonesModal, setShowMilestonesModal] =
    useState<boolean>(false);
  const [showAllMilestones, setShowAllMilestones] = useState<boolean>(false);
  const [showMilestonesViewBtn, setShowMilestonesViewBtn] =
    useState<boolean>(false);
  const [orderedPatientMilestones, setOrderedPatientMilestones] = useState<
    PatientMilestone[]
  >([]);
  const [initialOrderingComplete, setInitialOrderingComplete] = useState(false);
  const [deleteActionCompleted, setDeleteActionCompleted] =
    useState<boolean>(true);
  const {
    milestonesQuery,
    patientID,
    patientMilestonesQuery,
    setShowErrorToast,
    patientDataReadOnly,
  } = useContext(APIContext);
  const queryClient = useQueryClient();
  const patientMilestones = useMemo(
    () =>
      patientMilestonesQuery.data
        ? patientMilestonesQuery.data.filter(
            (pm) => !pm.milestone.is_clinical_marker
          )
        : [],
    [patientMilestonesQuery.data]
  );

  const existingOpenMilestoneIds = useMemo(() => {
    if (!patientMilestonesQuery.data) return new Set<number>();
    return new Set(
      patientMilestonesQuery.data
        .filter(
          (pm) =>
            pm.milestone_status === MilestoneStatusChoices.INCOMPLETE &&
            !pm.milestone.is_clinical_marker
        )
        .map((pm) => pm.milestone.id)
    );
  }, [patientMilestonesQuery.data]);

  const milestoneOptions = useMemo(() => {
    // Gate on both queries so existingOpenMilestoneIds is populated by the
    // time the Add-milestone UI renders — otherwise already-open milestones
    // would appear as selectable and handleSaveNewMilestones would treat them
    // as new, allowing duplicates.
    if (!milestonesQuery.data || !patientMilestonesQuery.data) return [];
    return milestonesQuery.data
      .filter((m) => !m.is_clinical_marker && m.should_display)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [milestonesQuery.data, patientMilestonesQuery.data]);

  const disableEdit =
    patientDataReadOnly ||
    configuration.milestones_section !==
      MilestonesSectionConfigurations.EDITABLE;

  // Add state to track pending mutations by PatientMilestone ID (for PATCH)
  // and milestone template ID (for POST of new milestones)
  const [pendingMilestoneIds, setPendingMilestoneIds] = useState<Set<number>>(
    new Set()
  );

  // Ticks so ages, and flags crossing the threshold, update while the card
  // stays open on unchanged data.
  const [now, setNow] = useState(() => moment());
  useEffect(() => {
    const id = setInterval(() => setNow(moment()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const patchMutation = useMutation(patchPatientMilestone, {
    onSuccess: (data) => {
      setDeleteActionCompleted(true);
      // Clear this milestone from pending
      setPendingMilestoneIds((prev) => {
        const next = new Set(prev);
        next.delete(data.id);
        return next;
      });
      queryClient.invalidateQueries({
        queryKey: ["patientmilestones", patientID],
      });
    },
    onError: (error, variables) => {
      setDeleteActionCompleted(true);
      // Clear this milestone from pending on error too
      setPendingMilestoneIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.id as number);
        return next;
      });
      setShowErrorToast(true);
    },
  });

  const postMutation = useMutation(postPatientMilestones, {
    onSuccess: (data) => {
      // Clear all pending milestone IDs
      setPendingMilestoneIds(new Set());
      queryClient.invalidateQueries({
        queryKey: ["patientmilestones", patientID],
      });
    },
    onError: () => {
      setPendingMilestoneIds(new Set());
      setShowErrorToast(true);
    },
  });

  const columns = useMemo(
    () => [
      {
        headerName: "",
        field: "milestone_status",
        renderCell: (row) => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                lineHeight: 0,
              }}
            >
              <MilestoneItem
                data={row}
                disabled={disableEdit}
                isPending={
                  pendingMilestoneIds.has(row.id) ||
                  pendingMilestoneIds.has(-row.milestone?.id)
                }
              />
            </Box>
          );
        },
      },
      {
        headerName: "",
        field: "milestone.display_name",
        renderCell: (row: PatientMilestone) => {
          const updatedBy = row.updated_by_name || row.updated_by;
          const timestamp = moment(row.qv_updated_ts);

          const hoursSinceOpen = now.diff(timestamp, "hours");
          const shouldFlag =
            row.milestone_status === MilestoneStatusChoices.INCOMPLETE &&
            row.milestone.should_flag_open &&
            configuration.open_milestone_flag_threshold != null &&
            hoursSinceOpen >= configuration.open_milestone_flag_threshold;
          // A flagged row already shows its age next to the name.
          const timestampStr = shouldFlag
            ? updatedBy
            : `${updatedBy}, ${timestamp.fromNow()}`;

          const handleDeleteMilestone = () => {
            const payload = {
              id: row.id,
              milestone_status: MilestoneStatusChoices.CANCELED,
            };
            patchMutation.mutate(payload);
            setDeleteActionCompleted(false);
            // optimistic update on the orderedPatientMilestones array
            const newOrderedPatientMilestones = orderedPatientMilestones.filter(
              (pm) => pm.id !== row.id
            );
            setOrderedPatientMilestones(newOrderedPatientMilestones);
            setShowMilestonesViewBtn(
              newOrderedPatientMilestones.length > MAX_INITIAL_ROWS
            );
          };

          return (
            <Box sx={sxStyles.valueWrapper}>
              <Tooltip
                title={row.secondary_formatted_value}
                placement="right-start"
                arrow
                disableHoverListener={
                  !configuration.display_milestone_secondary_value_tooltip
                }
                componentsProps={{
                  tooltip: {
                    sx: sxStyles.milestoneTooltip,
                  },
                }}
              >
                <Box sx={sxStyles.nameWrapper}>
                  <Typography
                    sx={sxStyles.key(
                      row.secondary_formatted_value &&
                        configuration.display_milestone_secondary_value_tooltip
                    )}
                  >
                    {row.milestone.display_name}
                  </Typography>
                  {shouldFlag && (
                    <Box sx={sxStyles.openFlag}>
                      <AccessTimeIcon sx={sxStyles.openFlagIcon} />
                      <Typography sx={sxStyles.openFlagText}>
                        {hoursSinceOpen}h ago
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Tooltip>
              {configuration.display_milestone_updated_timestamp && (
                <span className={styles.milestoneTimestamp}>
                  {timestampStr}
                </span>
              )}
              {!disableEdit && (
                <Tooltip
                  title="Remove this milestone"
                  placement="right-start"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: sxStyles.tooltip,
                    },
                  }}
                >
                  <IconButton
                    aria-label="delete-milestone"
                    sx={sxStyles.deleteIconWrapper}
                    disabled={!deleteActionCompleted}
                  >
                    {deleteActionCompleted ? (
                      <DeleteOutlineIcon
                        id="delete-icon"
                        className={styles.deleteIcon}
                        onClick={handleDeleteMilestone}
                      />
                    ) : (
                      <CircularProgress
                        id="delete-icon-progress"
                        size={16}
                        sx={{ color: "transparent" }}
                        className={cx(styles.deleteIcon)}
                      />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      disableEdit,
      configuration.display_milestone_secondary_value_tooltip,
      configuration.display_milestone_updated_timestamp,
      configuration.open_milestone_flag_threshold,
      now,
      orderedPatientMilestones,
      setOrderedPatientMilestones,
      deleteActionCompleted,
      pendingMilestoneIds,
    ]
  );

  const handleSaveNewMilestones = useCallback(
    (newMilestones: Milestone[]) => {
      const payload = [];
      const existingMilestones = patientMilestones.map((pm) => pm.milestone.id);
      const newOrderedPatientMilestones = [...orderedPatientMilestones];
      const newPendingIds = new Set(pendingMilestoneIds);

      for (const nm of newMilestones) {
        // if the patient already has a milestone we're trying to add,
        // just update the current milestone to incomplete if it's completed,
        // otherwise move on
        if (existingMilestones.includes(nm.id)) {
          const existing = patientMilestones.find(
            (pm) => pm.milestone.id === nm.id
          );

          // Track by PatientMilestone ID for PATCH
          newPendingIds.add(existing.id);

          const payload = {
            id: existing.id,
            milestone_status: MilestoneStatusChoices.INCOMPLETE,
            secondary_formatted_value: null,
          };
          patchMutation.mutate(payload);
          // optimistic update on the orderedPatientMilestones array
          const existingPMIndex = newOrderedPatientMilestones.findIndex(
            (pm) => pm.id === existing.id
          );
          if (existingPMIndex !== -1) {
            newOrderedPatientMilestones[existingPMIndex] = {
              ...existing,
              milestone_status: MilestoneStatusChoices.INCOMPLETE,
              secondary_formatted_value: null,
              qv_updated_ts: new Date().toISOString(),
            } as PatientMilestone;
          } else {
            newOrderedPatientMilestones.unshift({
              ...existing,
              milestone_status: MilestoneStatusChoices.INCOMPLETE,
              secondary_formatted_value: null,
              qv_updated_ts: new Date().toISOString(),
            });
          }
        } else {
          newPendingIds.add(-nm.id); // Track by milestone template ID for POST (use negative to distinguish)

          payload.push({
            milestone: nm.id,
            formatted_value: nm.display_name,
            milestone_status: MilestoneStatusChoices.INCOMPLETE,
            source: Sources.USER_CREATED,
            patient: patientID,
            calculated_status: MilestoneStatusChoices.INCOMPLETE,
            calculated_updated_ts: new Date(),
          });
          // optimistic update on the orderedPatientMilestones array
          newOrderedPatientMilestones.unshift({
            milestone: nm,
            formatted_value: nm.display_name,
            milestone_status: MilestoneStatusChoices.INCOMPLETE,
            source: Sources.USER_CREATED,
            patient: patientID,
            calculated_status: MilestoneStatusChoices.INCOMPLETE,
            calculated_updated_ts: new Date().toISOString(),
            qv_updated_ts: new Date().toISOString(),
          } as PatientMilestone);
        }
      }

      // Preserve the incomplete-then-completed grouping invariant used
      // elsewhere in this component, sorting by sort_order within each group.
      const sortedNewOrderedPatientMilestones = [
        ...newOrderedPatientMilestones.filter(isIncomplete).sort(bySortOrder),
        ...newOrderedPatientMilestones
          .filter((pm) => !isIncomplete(pm))
          .sort(bySortOrder),
      ];
      setOrderedPatientMilestones(sortedNewOrderedPatientMilestones);
      setPendingMilestoneIds(newPendingIds);
      setShowMilestonesViewBtn(
        sortedNewOrderedPatientMilestones.length > MAX_INITIAL_ROWS
      );
      if (payload.length > 0) {
        postMutation.mutate(payload);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      patientMilestones,
      orderedPatientMilestones,
      patientID,
      setOrderedPatientMilestones,
    ]
  );

  const milestonesModalRef = useRef<MilestonesModalHandle>(null);
  const addMilestonesRef = useRef<HTMLElement>(null);

  const milestonesSection = useMemo(() => {
    return (
      <Box>
        {!disableEdit && milestoneOptions.length > 0 && (
          <Box sx={sxStyles.addMilestoneWrapper} ref={addMilestonesRef}>
            <Box sx={sxStyles.buttonWrapper}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowMilestonesModal(true);
                  // Use setTimeout to ensure the modal is rendered before focusing
                  setTimeout(() => {
                    if (milestonesModalRef.current) {
                      milestonesModalRef.current.focusInput();
                    }
                  }, 0);
                }}
                sx={sxStyles.addButton(showMilestonesModal)}
              >
                Add milestone
              </Button>
            </Box>
            <MilestonesModal
              ref={milestonesModalRef}
              open={showMilestonesModal}
              anchorElement={addMilestonesRef.current}
              options={milestoneOptions}
              disabledOptionIds={existingOpenMilestoneIds}
              onSave={handleSaveNewMilestones}
              onCancel={() => setShowMilestonesModal(false)}
              onClose={() => setShowMilestonesModal(false)}
            />
          </Box>
        )}
        <Table
          columns={columns}
          rows={
            showAllMilestones
              ? orderedPatientMilestones
              : orderedPatientMilestones.slice(0, MAX_INITIAL_ROWS)
          }
          rowClass={styles.row}
          sx={{ ...sxStyles.table(disableEdit), ...sxStyles.tableBottom }}
          hideHeader
        ></Table>
        {showMilestonesViewBtn && (
          <Box sx={sxStyles.buttonWrapper}>
            <Divider sx={{ margin: "0.5rem 0rem 0.5rem 2rem" }} />
            <Button
              variant="text"
              onClick={() => setShowAllMilestones(!showAllMilestones)}
              sx={{
                width: "100%",
                color: deepPurple[400],
                textTransform: "none",
                fontSize: "0.875rem",
                padding: 0,
                "&:hover": {
                  backgroundColor: Colors.light_30,
                },
              }}
            >
              {showAllMilestones
                ? "View less"
                : `View more (${
                    orderedPatientMilestones.length - MAX_INITIAL_ROWS
                  })`}
            </Button>
          </Box>
        )}
      </Box>
    );
  }, [
    disableEdit,
    milestoneOptions,
    existingOpenMilestoneIds,
    showMilestonesModal,
    showAllMilestones,
    orderedPatientMilestones,
    showMilestonesViewBtn,
    columns,
    handleSaveNewMilestones,
    setShowMilestonesModal,
    setShowAllMilestones,
  ]);

  // Order milestones on the first render only
  useEffect(() => {
    if (!patientMilestonesQuery.data) return;
    const displayable = patientMilestonesQuery.data.filter(
      isDisplayableMilestone
    );
    const openPatientMilestones = displayable
      .filter(isIncomplete)
      .sort(bySortOrder);
    const completedPatientMilestones = displayable
      .filter((pm) => !isIncomplete(pm))
      .sort(bySortOrder);
    const orderedPM = [...openPatientMilestones, ...completedPatientMilestones];
    setOrderedPatientMilestones(orderedPM);
    setShowMilestonesViewBtn(orderedPM.length > MAX_INITIAL_ROWS);
    setInitialOrderingComplete(true);
    // eslint-disable-next-line
  }, []);

  // Update milestones when the patient-milestones query data changes
  useEffect(
    () => {
      if (!patientMilestonesQuery.data || !initialOrderingComplete) return;

      const currentPatientMilestones = patientMilestonesQuery.data.filter(
        isDisplayableMilestone
      );

      const currentPatientMilestonesMap = new Map<number, PatientMilestone>(
        currentPatientMilestones.map((pm) => [pm.id, pm])
      );

      // Refresh in-place: keep existing order, drop disappeared items, swap in fresh data.
      const updatedExisting = orderedPatientMilestones.flatMap((pm) => {
        const fresh = currentPatientMilestonesMap.get(pm.id);
        return fresh ? [fresh] : [];
      });

      const orderedPatientMilestonesIds = new Set(
        orderedPatientMilestones.map((pm) => pm.id)
      );
      const newPatientMilestones = currentPatientMilestones.filter(
        (pm) => !orderedPatientMilestonesIds.has(pm.id)
      );

      // Common path: no new milestones, just refreshed values — keep existing order.
      // Otherwise: slot new milestones into their sort_order position within each group.
      let next: PatientMilestone[];
      if (newPatientMilestones.length === 0) {
        next = updatedExisting;
      } else {
        const existingIncomplete = updatedExisting.filter(isIncomplete);
        const existingCompleted = updatedExisting.filter(
          (pm) => !isIncomplete(pm)
        );
        const newIncomplete = newPatientMilestones.filter(isIncomplete);
        const newCompleted = newPatientMilestones.filter(
          (pm) => !isIncomplete(pm)
        );

        // bySortOrder is fully deterministic (sort_order, then display_name),
        // and Array.prototype.sort is stable (ES2019), so items that tie on both
        // keys retain their existing relative order without a manual tiebreak.
        const mergedIncomplete = [...existingIncomplete, ...newIncomplete].sort(
          bySortOrder
        );
        const mergedCompleted = [...existingCompleted, ...newCompleted].sort(
          bySortOrder
        );
        next = [...mergedIncomplete, ...mergedCompleted];
      }

      // React Query's structuralSharing keeps the same object reference when an
      // item is byte-for-byte unchanged, so reference equality covers every
      // rendered field (status, timestamps, updated_by_name, formatted values,
      // etc.) without enumerating them.
      const needsUpdate =
        next.length !== orderedPatientMilestones.length ||
        next.some((pm, idx) => orderedPatientMilestones[idx] !== pm);

      if (needsUpdate) {
        setOrderedPatientMilestones(next);
        setShowMilestonesViewBtn(next.length > MAX_INITIAL_ROWS);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientMilestonesQuery.data, initialOrderingComplete]
  );

  return (
    <Box className={cx(styles.roundedSection, styles.milestonesSection)}>
      <Box
        sx={{ ...sxStyles.headingWrapper, marginTop: 0 }}
        onClick={() => setShowSection(!showSection)}
      >
        <Typography
          sx={{ ...sxStyles.sectionHeading, ...sxStyles.headingWithIcon }}
        >
          <Icons.ListIcon className={styles.headerIcon} />
          <span>Milestones</span>
        </Typography>
        <Box sx={{ display: "flex" }}>
          {configuration.display_milestone_updated_disclaimer && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AutorenewIcon sx={sxStyles.refreshIcon} />
              <Typography sx={sxStyles.secondaryValue}>
                Data updates every 30m
              </Typography>
            </Box>
          )}
          {!showSection && (
            <ExpandMoreIcon className={styles.expandCollapseIcon} />
          )}
          {showSection && (
            <ExpandLessIcon className={styles.expandCollapseIcon} />
          )}
        </Box>
      </Box>
      <Collapse in={showSection}>
        <SectionGrid>{milestonesSection}</SectionGrid>
      </Collapse>
    </Box>
  );
};

export default MilestonesSection;
